import icons from '@groovepages/gd-ui-kit/assets/icons-canvas-toolbar.svg?fileloader';
import shortid from 'shortid';
import Element from './elements/Element';
import i18 from './i18n';

const utils = {
  gpAttributesString:
    '[data-gp-style], [data-gp-text], [data-gp-component], [data-gp-dnd]',
  gpAttributesArray: [
    'data-gp-style',
    'data-gp-text',
    'data-gp-component',
    'data-gp-dnd',
    'data-gp-attributes',
  ],
  breakpointLabels(breakpoints) {
    const breakpointValues = [...Object.values(breakpoints)]
      .map((breakpoint) => parseInt(breakpoint.replace(/\D/g, ''), 10))
      .sort((a, b) => {
        return a - b;
      });
    const mapped = [];
    mapped[0] = `(max-width: ${breakpointValues[1] - 1}px)`;
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i < breakpointValues.length - 1; i++) {
      mapped[i] = `(max-width: ${
        breakpointValues[i + 1] - 1
      }px) and (min-width: ${breakpointValues[i]}px)`;
    }
    mapped[breakpointValues.length - 1] = `(min-width: ${
      breakpointValues[breakpointValues.length - 1]
    }px)`;
    return mapped;
  },
  getMenuButton(icon, {text, iconText} = {}) {
    const button = document.createElement('button');
    const iconHTML = `<svg class="gp-menu-button-icon" data-gp-menu-${icon}>
      <use href="${icons}#${icon}" xlink:href="/images/${icons}.svg?fileloader#${icon}"></use>
      ${iconText ? `<text x="50%" y="50%">${iconText}</text>` : ''}
    </svg>`;
    const textHTML = `<span class="gp-menu-button-text">${text}</span>`;
    button.classList.add('gp-menu-button');
    button.innerHTML = `
      ${text ? textHTML : ''}
      ${icon ? iconHTML : ''}
    `;
    button.classList.add('gp-component-menu-button', 'focus:outline-none');
    return button;
  },
  createMenuButton({ classes, text, icon, iconText, title, callback }) {
    const button = this.getMenuButton(icon, {text, iconText});
    if (classes) button.classList.add(...classes);
    button.setAttribute('title', title);
    button.addEventListener('click', callback);
    return button;
  },
  addMenuButton(menus, attrs) {
    const button = this.createMenuButton(attrs);
    const index = menus.findIndex(m => m.getAttribute('title') === attrs.title);
    if (index > -1) {
      menus.splice(index, 1);
    }
    menus.push(button);
    return button;
  },

  getComponentLabel(element) {
    if (element.hasAttribute('data-gp-component-label')) {
      return element.getAttribute('data-gp-component-label');
    }
    if (element.hasAttribute('data-gp-block')) return 'Block';
    if (element.hasAttribute('data-gp-container')) {
      return 'Container';
    }
    if (
      element.hasAttribute('data-gp-component') &&
      element.getAttribute('data-gp-component') !== 'null'
    ) {
      return element.getAttribute('data-gp-component');
    }
    if (element.hasAttribute('data-gp-text')) return 'Text';
    if (element.hasAttribute('data-gp-style')) {
      return 'StyleableElement';
    }
    if (element.hasAttribute('data-gp-dnd')) {
      return 'DraggableElement';
    }
  },
  triggerEvent(el, eventName, options) {
    const event = new CustomEvent(eventName, { detail: options });
    el.dispatchEvent(event);
  },
  reinitializeScripts(content, context = null) {
    const load = [];
    const scripts = content.querySelectorAll('script:not(.gp-script-reinit)');
    for (let i = 0; i < scripts.length; i++) {
      if (!scripts[i].id) scripts[i].id = `script-${  shortid.generate()}`;
      if (content.querySelector(`.gp-script-reinit-${scripts[i].id}`)) {
        continue;
      }
      const script = document.createElement('SCRIPT');
      script.classList.add(
        `gp-script-reinit-${scripts[i].id}`,
        'gp-script-reinit'
      );
      if (scripts[i].hasAttribute('src')) {
        script.setAttribute('src', scripts[i].getAttribute('src'));
        load.push(
          new Promise((resolve) => {
            script.addEventListener('load', () => {
              resolve();
            });
          })
        );
      } else script.innerHTML = scripts[i].innerHTML;
      try {
        if (context) {
          context.body.appendChild(script);
        } else {
          content.body.appendChild(script);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('embed error');
        // eslint-disable-next-line no-console
        console.log(scripts[i].innerHTML);
      }
    }
    return new Promise((resolve) => {
      Promise.all(load).then(() => {
        resolve();
      });
    });
  },
  wrapElements(elementToCheck) {
    const selectorToCheck = '[data-gp-text]';
    const classToAdd = 'gp-text-container';
    elementToCheck.querySelectorAll(selectorToCheck).forEach((element) => {
      if (!element.classList.contains(classToAdd)) {
        const wrapper = document.createElement('div');
        wrapper.classList.add(classToAdd);
        element.parentNode.replaceChild(wrapper, element);
        element.removeAttribute('data-gp-component-id');
        element.removeAttribute('data-gp-component');
        element.classList.remove('gp-component');
        wrapper.appendChild(element);
        utils.gpAttributesArray.forEach((attribute) => {
          if (element.hasAttribute(attribute)) {
            const value = element.getAttribute(attribute);
            element.removeAttribute(attribute);
            wrapper.setAttribute(attribute, value);
          }
        });
      }

      const nestedParent = element.parentNode.parentNode;
      if( nestedParent && nestedParent.classList.contains(classToAdd) )
        nestedParent.replaceWith(...nestedParent.childNodes);
    });
  },
  getComponentIdFromClass(element) {
    let id = null;
    element.classList.forEach((className) => {
      if (className.indexOf('gp-component-id-') !== -1)
        id = className.replace('gp-component-id-', '');
    });
    return id;
  },
  reflashComponentAttributes(element) {
    [...element.classList].forEach((className) => {
      if (className.indexOf('data-gp-attribute-') === -1) return;
      const attribute = className.replace('data-gp-attribute-', '');
      const value = [...element.classList].find(
        (valueClass) =>
          valueClass.indexOf(`data-gp-attributevalue-${attribute}`) !== -1
      );
      if (value)
        element.setAttribute(
          attribute,
          value.replace(`data-gp-attributevalue-${attribute}-`, '')
        );
    });
  },
  clearComponentIdClass(element) {
    const toClear = [];
    element.classList.forEach((className) => {
      if (className.indexOf('gp-component-id-') !== -1) toClear.push(className);
    });
    toClear.forEach((className) => {
      element.classList.remove(className);
      element.querySelectorAll(`.${className}`).forEach((child) => {
        child.classList.remove(className);
      });
    });
  },
  clearOrphanedStyles(canvas) {
    canvas
      .querySelectorAll('style[data-gp-styled-element]')
      .forEach((stylesheet) => {
        if (stylesheet.classList.contains('style-gp-body')) return;
        const element = stylesheet.getAttribute('data-gp-styled-element');
        if (
          !canvas.querySelector(`.gp-component-id-${element}`) ||
          stylesheet.className.includes('gp-component-id')
        ) {
          stylesheet.remove();
        }
      });
  },
  componentCloned(cloneElement, eventBus) {
    this.setupClonedComponent(cloneElement, eventBus);
    cloneElement
    .querySelectorAll(
      `${this.gpAttributesString  }, [class*="gp-component-id-"]`
    )
    .forEach((element) => {
      this.setupClonedComponent(element, eventBus);
    });
  },
  setupClonedComponent(element, eventBus) {
    if (
      element.classList.contains('ql-editor') ||
      element.classList.contains('ql-clipboard') ||
      element.classList.contains('ql-tooltip')
    )
      return;
    const newId = this.uniqueId();
    const oldId = this.getComponentIdFromClass(element);
    const componentSpecificAttr = [
      'data-gp-countdown',
      'data-gp-tabs-id',
      'data-gp-slider'
    ].find((el) => element.hasAttribute(el));
    const reg = componentSpecificAttr
      ? `${oldId}|${element.getAttribute(componentSpecificAttr)}`
      : oldId;
    this.clearComponentIdClass(element);
    element.classList.add(`gp-component-id-${newId}`);

    if (element.getAttribute('data-gp-component-id')) {
      element.setAttribute('data-gp-component-id', newId);
      Array.from(element.children).forEach(child => {
        if (child.matches('script') && !child.hasAttribute('src')) {
          child.removeAttribute('id');
          child.innerHTML = child.innerHTML.replaceAll(
            new RegExp(reg, 'g'),
            newId
          );
        }
      });
      if (componentSpecificAttr) {
        element.setAttribute(componentSpecificAttr, newId);
      }
      eventBus.$emit('component-cloned', {
        from: oldId,
        to: newId,
      });
    } else {
      eventBus.$emit('text-node-cloned', {
        from: oldId,
        to: newId,
      });
    }
  },
  setupComponents(canvas) {
    canvas.querySelectorAll(this.gpAttributesString).forEach((element) => {
      if (element.getAttribute('data-gp-component-id')) {
        return;
      }
      const id = this.getComponentIdFromClass(element) || this.uniqueId();
      element.setAttribute('data-gp-component-id', id);
      element.classList.add('gp-component');
      element.classList.add(`gp-component-id-${id}`);
    });
  },
  setupBlockAdders(canvas) {
    const plusIcon = `<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M376 232H216V72c0-4.42-3.58-8-8-8h-32c-4.42 0-8 3.58-8 8v160H8c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h160v160c0 4.42 3.58 8 8 8h32c4.42 0 8-3.58 8-8V280h160c4.42 0 8-3.58 8-8v-32c0-4.42-3.58-8-8-8z"/></svg>`;
    const blocks = canvas.querySelectorAll('[data-gp-block]');
    const psudoBlocks = canvas.querySelectorAll('.swiper-slide-content, .gp-tab-content');
    const previousAdders = canvas.querySelectorAll('.gp-blocks-adder');
    const blocksContainer = canvas.getElementById('blocks-container');
    const jumboAdder = `<div class="gp-blocks-adder" id="blocks-adder">
      <span class="gp-blocks-adder-title text-black">${i18.t('frontend_builder_blocks_adder_title')}</span>
      <button title="add block" class="gp-blocks-adder-btn hover:bg-primary-dark shadow-lg" onclick="window.parent.dispatchEvent(new CustomEvent('blocks-adder'))">
        ${plusIcon}
      </button>
    </div>`;

    previousAdders.forEach(adder => adder.remove());

    if (!blocks.length) {
      blocksContainer.insertAdjacentHTML('beforeend', jumboAdder);
    }

    blocks.forEach((block,idx) => {
      block.removeAttribute('data-gp-dnd');
      if (block.querySelector('.data-gp-navmenu-container'))
        block.classList.add('gp-navmenu-block');
      else block.classList.remove('gp-navmenu-block');
      if (idx === 0) {
        const style = block.classList.contains('fix-top') ? `style="margin-bottom: ${block.clientHeight}px"` : '';
        const blockAdderBefore = `<div class="gp-blocks-adder gp-blocks-adder--inner" ${style}>
          <button title="add block" class="gp-blocks-adder-btn gp-blocks-adder-btn--top hover:bg-primary-dark shadow-lg" onclick="window.parent.dispatchEvent(new CustomEvent('blocks-adder', {detail: {blockId: '${block.getAttribute('data-gp-component-id')}', before: true}}))">
            ${plusIcon}
          </button>
        </div>`;
        block.insertAdjacentHTML('beforebegin', blockAdderBefore);
      }
      const blockAdder = `<div class="gp-blocks-adder gp-blocks-adder--inner">
        <button title="add block" class="gp-blocks-adder-btn gp-blocks-adder-btn--${idx === blocks.length - 1 ? 'bottom' : 'inner'} hover:bg-primary-dark shadow-lg" onclick="window.parent.dispatchEvent(new CustomEvent('blocks-adder', {detail: {blockId: '${block.getAttribute('data-gp-component-id')}'}}))">
          ${plusIcon}
        </button>
      </div>`;
      block.insertAdjacentHTML('afterend', blockAdder);
    });

    setTimeout(() => {
      psudoBlocks.forEach(block => {
        if (!block.childElementCount) {
          const id = this.quillSafeID(shortid.generate());
          const tabContentId = block.getAttribute('data-gp-tab-content'); // in case of tab-content block
          const blockAdder = `<div class="gp-blocks-adder gp-blocks-adder--inner gp-blocks-adder--psudo" data-gp-psudo-block="${id}">
            <span class="gp-blocks-adder-title text-black">Add Block ${tabContentId ? `for ${tabContentId}` : ''}</span>
            <button title="add block" class="gp-blocks-adder-btn hover:bg-primary-dark shadow-lg" onclick="window.parent.dispatchEvent(new CustomEvent('blocks-adder', {detail: {selector: 'data-gp-psudo-block', blockId: '${id}', before: true}}))">
              ${plusIcon}
            </button>
          </div>`;
          block.insertAdjacentHTML('afterbegin', blockAdder);
        }
      });
    }, 0);
  },
  checkForDuplicates(content) {
    const canvas = content.ownerDocument || content;
    const duplicates = [];
    content
      .querySelectorAll('[class*="gp-component-id-"]')
      .forEach((component) => {
        const id = this.getComponentIdFromClass(component);
        const elements = canvas.querySelectorAll(`.gp-component-id-${id}`);
        if (elements.length > 1) {
          duplicates.push(elements[elements.length - 1]);
        }
      });
    return [...new Set(duplicates)];
  },
  getNode(id, canvas) {
    return canvas.querySelector(`[data-gp-component-id="${id}"]`);
  },
  getComponent(id, eventBus, canvas) {
    const node = canvas.querySelector(`[data-gp-component-id="${id}"]`);
    if (!node) return null;
    return new Element(node, eventBus);
  },
  mergeOverlapping(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] !== mergeTo[key]) {
          mergeTo[key] = null;
        }
      }
    };
    merge(toMerge, value);
  },
  removeOverlapping(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] === mergeTo[key]) {
          mergeTo[key] = null;
        }
      }
    };
    merge(toMerge, value);
  },
  mergeOverwritten(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] !== null) {
          mergeTo[key] = mergeFrom[key];
        }
      }
    };
    merge(toMerge, value);
  },
  uniqueId() {
    return this.quillSafeID(shortid.generate());
  },
  quillSafeID(id) {
    return id.split('-').join('0');
  },
  cleanCanvas(content,ignoreImg = false) {
    if( !ignoreImg ) for (const el of content.getElementsByClassName('dndbefore')) this.resetFigure(el);
    for (const el of content.getElementsByClassName('data-gp-countdown-switch')) el.remove();
    for (const el of content.getElementsByClassName('gp-component-menu')) el.style.left = 0;
    for (const el of content.querySelectorAll('[data-gp-quick-popup-id]')) el.remove();
  },
  resetFigure(el) {
    if( el.tagName == 'FIGURE' && el.classList.contains('dndbefore') ){
      const img = el.childNodes[0];
      el.parentNode.insertBefore(img, el);

      for (let index = el.attributes.length - 1; index > -1; -- index) {
        const attribute = el.attributes[index];
        if( attribute.name !== 'src' && attribute.name !== 'width' )
          img.setAttribute(attribute.name, attribute.value);
      }

      el.remove();
    }

    el.classList.remove('dndbefore');
    el.classList.remove('dndbefore-top');
    el.classList.remove('dndbefore-middle');
    el.classList.remove('dndbefore-bottom');
  },
  getPrevSibling(elem, selector) {
    let sibling = elem.previousElementSibling;

    if (!selector) return sibling;

    while (sibling) {
      if (sibling.matches(selector)) return sibling;
      sibling = sibling.previousElementSibling;
    }

    return null;
  },
  getNextSibling(elem, selector) {
    let sibling = elem.nextElementSibling;

    if (!selector) return sibling;

    while (sibling) {
      if (sibling.matches(selector)) return sibling;
      sibling = sibling.nextElementSibling;
    }

    return null;
  },
  triggerManualEvent(el, type, eventType = 'MouseEvents'){
    if ('createEvent' in document) {
      const e = document.createEvent(eventType);
      e.initEvent(type, false, true);
      el.dispatchEvent(e);
    }
 }
};
export default utils;
